import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, merge } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { ScrollService } from '../../../services/scroll.service';
import { LocalstorageService } from '../../../services/localstorage.service';
import { ArrayService } from '../../../services/array.service';
import { PlatformService } from 'src/app/core/services/platform.service';
import { NotificationType } from '../../../types/notifications-type';
import { CustomNotificationChanelEnum } from '../../../enums/notifications-enum';
import { CookieService } from 'ngx-unificator/services';
import { CustomNotificationCenterService } from './custom-notification-center.service';
import { InstallAppService } from '../../../services/install-app.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService {

  private _scroll = inject(ScrollService);
  private _localStorage = inject(LocalstorageService);
  private _array = inject(ArrayService);
  private _platform = inject(PlatformService);
  private _cookie = inject(CookieService);
  private _customNotify = inject(CustomNotificationCenterService);
  private _installApp = inject(InstallAppService);

  public sideNavOpen = signal(false);
  public isNotUnique = signal(false);
  public isOpened = signal(false);

  /**
   * Notification subject
   */
  public notifications$: BehaviorSubject<NotificationType[]> = new BehaviorSubject<any>([]);

  /**
   * Init notifications
   */
  public initNotify() {
    if (this._platform.isBrowser) {
      this._localStorage.clearItem('notifications');
      merge(...this._customNotify.notificationSubjectList$).pipe(
        filter(notification => !!notification),
        map(notification => Array.isArray(notification) ? notification : [notification]),
        tap((notificationList) => {
          const current = this.notifications$.getValue();

          const newList = current.filter(n =>
            !notificationList.some(newItem => newItem.notify_channel === n.notify_channel),
          );

          this.notifications$.next([...notificationList, ...newList]);
        }),
      ).subscribe();
    }
  }

  /**
   * Open sidenav menu
   */
  public open() {
    if (!this.sideNavOpen()) {
      this.sideNavOpen.set(true);
      this._markNotifyRead();
      this.isOpened.set(true);
      this._scroll.blockScroll();
    }
  }

  /**
   * Close sidenav menu
   */
  public close() {
    if (this.sideNavOpen()) {
      this.sideNavOpen.set(false);
      this._scroll.UnblockScroll();
      this._markNotifyRead();
    }
  }

  /**
   * Toggle sidenav menu
   */
  public toggle() {
    this.sideNavOpen() ? this.close() : this.open();
  }

  public removeNotify(notify: NotificationType) {
    const current = this.notifications$.getValue();
    const filtered = current.filter(n => n.uid !== notify.uid);

    this.notifications$.next(filtered);

    if (!filtered.length) {
      this.toggle();
    }

    const channel = notify?.notify_channel;

    switch (channel) {
      case CustomNotificationChanelEnum.BLOG_NOTIFICATIONS:
        if (notify.slug) {
          this._cookie.set(notify.slug, '1');
        }
        break;
      case CustomNotificationChanelEnum.INSTALL_APP:
        this._installApp.closeNotificationBlock();
        break;
    }
  }

  private _markNotifyRead() {
    this._checkUniqueNotify();
    this._localStorage.set('notifications', JSON.stringify(this.notifications$.getValue()));
  }

  private _checkUniqueNotify() {
    const notifyArr: any = JSON.parse(this._localStorage.get('notifications'));
    if (!notifyArr) {
      return;
    }
    const notifyArrNow = this.notifications$.getValue();

    const result = this._array.arrayDifference(notifyArr, notifyArrNow, (o1, o2) => o1.id === o2.id);

    if (result.length > 0) {
      this.isNotUnique.set(false);
      this.isOpened.set(false);
    } else {
      this.isNotUnique.set(true);
    }
  }
}
